// Plan post-checks shared by every AI goal path: short titles (the full wording
// moves to the goal's description) and no placeholder tasks. Pure TS.
import { deadlineFromGoal } from './goalDeadline'

export const GOAL_TITLE_MAX = 60
export const MILESTONE_TITLE_MAX = 60
export const TARGET_TITLE_MAX = 50
const DESCRIPTION_MAX = 1000

const CLAUSE_BREAK =
  /\s*(?:[,;:(]|\s[-–—]\s|、|，|；|：|\s(?:while|whilst|and|&|but|so that|because|plus|as well as|y|e|et|mientras|pendant que|enquanto)\s)/gi
const TRAILING_FILLER =
  /(?:\s+(?:and|or|while|whilst|to|for|of|the|a|an|with|by|in|on|at|&|y|e|et|de|del|la|le|les|el|para|pour|com|em))+$/i

const tidy = (s: string) =>
  s
    .replace(TRAILING_FILLER, '')
    .replace(/[\s,;:、，；：.\-–—(&]+$/u, '')
    .trim()

// Cuts at the last clause break that fits, else the last word that fits; text
// without spaces (ja/zh) is cut hard. A deadline the cut dropped is put back
// when it fits ("…by January").
export function shortenTitle(text: string, max: number, locale?: string): string {
  const s = text.replace(/\s+/g, ' ').trim()
  if (s.length <= max) return s
  const due = deadlineFromGoal(s, Date.now(), locale)
  if (due) {
    const room = max - due.phrase.length - 1
    const head = room >= 15 ? cutTo(s.replace(due.phrase, ' ').replace(/\s+/g, ' '), room) : ''
    if (head.length >= 15) return `${head} ${due.phrase}`
  }
  return cutTo(s, max)
}

function cutTo(s: string, max: number): string {
  if (s.length <= max) return tidy(s)
  let cut = ''
  for (const m of s.matchAll(CLAUSE_BREAK)) {
    if (m.index > max) break
    if (m.index >= Math.min(15, max / 3)) cut = s.slice(0, m.index)
  }
  if (!cut) {
    const space = s.lastIndexOf(' ', max)
    cut = space >= max / 2 ? s.slice(0, space) : `${s.slice(0, max - 1)}…`
  }
  return tidy(cut) || s.slice(0, max)
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

type PlanShape = {
  title: string
  description?: string
  milestones: Array<{ title: string; targets: Array<{ title: string; tasks: unknown[] }> }>
}

const MONTH =
  '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|janvier|f[ée]vrier|mars|avril|mai|juin|juillet|ao[uû]t|septembre|octobre|novembre|d[ée]cembre|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|janeiro|fevereiro|março|maio|junho|julho|setembro|outubro|dezembro)'
const MONTH_YEAR = new RegExp(`(\\b${MONTH}\\.?),?\\s+(?:de\\s+)?((?:19|20)\\d{2})\\b`, 'giu')

// The model likes to pin a year on a month the user wrote ("by January" →
// "by January 2027"); the deadline field already carries the date.
export function dropAddedYear(title: string, goalText: string): string {
  return title
    .replace(MONTH_YEAR, (m, month: string, year: string) => (goalText.includes(year) ? m : month))
    .replace(/((?:19|20)\d{2})年(?=\d{1,2}月)/g, (m, year: string) =>
      goalText.includes(year) ? m : '',
    )
}

// Clamps goal/milestone/target titles. The goal's description keeps the user's
// full wording whenever the title had to give some of it up.
export function enforcePlanTitles<T extends PlanShape>(
  tree: T,
  goalText: string,
  locale?: string,
): T {
  const raw = dropAddedYear(tree.title.replace(/\s+/g, ' ').trim(), goalText)
  const title = shortenTitle(raw, GOAL_TITLE_MAX, locale)
  const given = tree.description?.trim()
  const full = goalText.trim().length >= raw.length ? goalText.trim() : raw
  let description = given || (title !== raw ? full : undefined)
  if (description && norm(description) === norm(title)) description = undefined
  const { description: _drop, ...rest } = tree
  return {
    ...rest,
    title,
    ...(description && { description: description.slice(0, DESCRIPTION_MAX) }),
    milestones: tree.milestones.map((m) => ({
      ...m,
      title: shortenTitle(m.title, MILESTONE_TITLE_MAX, locale),
      targets: m.targets.map((t) => ({
        ...t,
        title: shortenTitle(t.title, TARGET_TITLE_MAX, locale),
      })),
    })),
  } as T
}

const ORDINAL =
  '(?:first|second|third|fourth|fifth|sixth|seventh|next|last|1st|2nd|3rd|4th|5th|6th|7th)'
const LEFT = '(?:remaining|outstanding|pending|leftover|unfinished|incomplete)'
const ITEM = '(?:project|task|item|module|assignment|exercise|course|lesson|chapter|deliverable)'
const PLACEHOLDER_TASK: RegExp[] = [
  new RegExp(`\\b${ORDINAL}\\s+${LEFT}\\b`, 'i'),
  new RegExp(`\\b${LEFT}\\s+${ITEM}s?\\s*(?:#\\s*)?(?:\\d+\\b|\\(\\d+\\))`, 'i'),
  /\[[^\]]{2,40}\]|<[^>]{2,40}>|\b(?:TBD|TBA)\b/,
  /\b(?:primer|segundo|tercer|pr[oó]ximo)\w*\s+\w+\s+(?:restante|pendiente)/i,
  /\b(?:premi[eè]re?|deuxi[eè]me|troisi[eè]me|prochain)\w*\s+\w+\s+restante?/i,
  /\b(?:primeir|segund|terceir|pr[oó]xim)\w*\s+\w+\s+(?:restante|pendente)/i,
  /(?:1|2|3|一|二|三)つ目の残り|残りの(?:1|2|3|一|二|三)つ目|第[一二三]个剩余|剩下的第[一二三]/,
]

export function isPlaceholderTask(title: string): boolean {
  return PLACEHOLDER_TASK.some((re) => re.test(title))
}

export type TaskPath = { m: number; t: number; k: number; title: string }

export function findPlaceholderTasks(tree: {
  milestones: Array<{ targets: Array<{ tasks: Array<{ title: string }> }> }>
}): TaskPath[] {
  return tree.milestones.flatMap((ms, m) =>
    ms.targets.flatMap((tg, t) =>
      tg.tasks.flatMap((task, k) =>
        isPlaceholderTask(task.title) ? [{ m, t, k, title: task.title }] : [],
      ),
    ),
  )
}

// Swaps in rewritten titles for the flagged tasks; a rewrite that is empty or
// still a placeholder leaves the original. Placeholders left over that repeat
// one pattern in a target collapse into the first, so "first/second/third
// remaining project" can't survive as three copies.
export function replaceTasks<
  T extends { milestones: Array<{ targets: Array<{ tasks: Array<{ title: string }> }> }> },
>(tree: T, flagged: TaskPath[], rewrites: string[]): T {
  const next = JSON.parse(JSON.stringify(tree)) as T
  flagged.forEach((p, i) => {
    const title = rewrites[i]?.replace(/\s+/g, ' ').trim()
    if (title && !isPlaceholderTask(title))
      next.milestones[p.m].targets[p.t].tasks[p.k].title = title
  })
  for (const ms of next.milestones)
    for (const tg of ms.targets) {
      const left = tg.tasks.filter((x) => isPlaceholderTask(x.title))
      if (left.length > 1 && left.length < tg.tasks.length)
        tg.tasks = tg.tasks.filter((x) => !isPlaceholderTask(x.title) || x === left[0])
    }
  return next
}
